import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/components/SimpleToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Users, Plus, Edit, Trash2, Mail, User, Eye, EyeOff, Shield, ShieldCheck, Users2,
  MoreHorizontal, UserCheck, UserX, Link2, Copy, Send, Sliders, CheckCircle2, Clock,
} from "lucide-react";
import api, { teamsApi, usersApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RbacMatrixDialog } from "@/components/user-management/RbacMatrixDialog";

const ROLE_LABELS = { admin: "Administrator", manager: "Manager", member: "Mitarbeiter", readonly: "Nur Lesen" };

const ROLE_BADGE = {
  admin: { icon: ShieldCheck, cls: "bg-primary/10 text-primary border-primary/20 hover:bg-primary/10" },
  manager: { icon: Shield, cls: "bg-info/10 text-info border-info/20 hover:bg-info/10" },
  member: { icon: User, cls: "" },
  readonly: { icon: Eye, cls: "bg-muted text-muted-foreground border-border" },
};

const userStatus = (u) => {
  if (u.invite_pending) return "invited";
  if (u.is_active === false) return "inactive";
  return "active";
};

export default function UserManagement() {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [rbacUser, setRbacUser] = useState(null);

  // Einladungslink-Dialog
  const [inviteLink, setInviteLink] = useState(null); // { url, name, expires }

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    full_name: "",
    password: "",
    role: "member",
    team_id: "",
  });

  const fetchUsers = useCallback(async () => {
    try {
      const [uRes, tRes] = await Promise.all([api.get("/users"), teamsApi.getAll().catch(() => ({ data: [] }))]);
      setUsers(Array.isArray(uRes.data) ? uRes.data : []);
      setTeams(Array.isArray(tRes.data) ? tRes.data : []);
    } catch (error) {
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";

  const addTeam = async () => {
    if (!newTeamName.trim()) return;
    try { await teamsApi.create(newTeamName.trim()); setNewTeamName(""); toast.success("Team erstellt"); fetchUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Team konnte nicht erstellt werden"); }
  };
  const removeTeam = async (id) => {
    if (!window.confirm("Team wirklich löschen? Zuordnungen der Nutzer werden entfernt.")) return;
    try { await teamsApi.delete(id); toast.success("Team gelöscht"); fetchUsers(); }
    catch { toast.error("Team konnte nicht gelöscht werden"); }
  };

  const resetForm = () => {
    setFormData({ username: "", email: "", full_name: "", password: "", role: "member", team_id: "" });
    setEditUser(null);
    setShowPassword(false);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      password: "",
      role: user.role || "member",
      team_id: user.team_id || "",
    });
    setDialogOpen(true);
  };

  const showInviteLink = (data, name) => {
    const url = `${window.location.origin}${data.invite_url_path}`;
    setInviteLink({ url, name, expires: data.invite_expires_at });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editUser) {
        const updateData = {};
        if (formData.username !== editUser.username) updateData.username = formData.username;
        if (formData.email !== editUser.email) updateData.email = formData.email;
        if (formData.full_name !== editUser.full_name) updateData.full_name = formData.full_name;
        if (formData.role !== (editUser.role || "member")) updateData.role = formData.role;
        if ((formData.team_id || "") !== (editUser.team_id || "")) updateData.team_id = formData.team_id;
        if (formData.password) updateData.password = formData.password;
        await api.put(`/users/${editUser.id}`, updateData);
        toast.success("Benutzer aktualisiert");
      } else {
        // Einladung: Nutzer ohne Passwort anlegen → kopierbarer Link
        const res = await usersApi.invite({
          username: formData.username,
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          team_id: formData.team_id || null,
        });
        toast.success("Einladung erstellt");
        showInviteLink(res.data, formData.full_name);
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvite = async (user) => {
    try {
      const res = await usersApi.resendInvite(user.id);
      showInviteLink(res.data, user.full_name);
      toast.success("Neuer Einladungslink erstellt");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Link konnte nicht erneuert werden");
    }
  };

  const handleDeactivate = async (user) => {
    try {
      await usersApi.deactivate(user.id);
      toast.success("Benutzer deaktiviert");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Deaktivierung fehlgeschlagen");
    }
  };

  const handleActivate = async (user) => {
    try {
      await usersApi.activate(user.id);
      toast.success("Benutzer reaktiviert");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reaktivierung fehlgeschlagen");
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/users/${userToDelete.id}`);
      toast.success("Benutzer endgültig gelöscht");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const copyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink.url);
      toast.success("Link kopiert");
    } catch {
      toast.error("Kopieren nicht möglich – bitte manuell markieren");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center" data-testid="no-access">
        <ShieldCheck className="w-14 h-14 text-muted-foreground/30" />
        <h2 className="text-xl font-semibold mt-4">Kein Zugriff</h2>
        <p className="text-muted-foreground mt-1">Nur Administratoren können die Benutzerverwaltung öffnen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Benutzer & Teams</h1>
          <p className="text-muted-foreground text-sm">
            Verwalten Sie Benutzer, Rollen, Rechte und Teams des Systems
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-user-btn">
          <Send className="w-4 h-4 mr-2" />
          Benutzer einladen
        </Button>
      </div>

      {/* Teams */}
      <Card data-testid="teams-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users2 className="w-5 h-5" />Teams ({teams.length})</CardTitle>
          <CardDescription>Teams für Zuweisungen und Sichtbarkeit von Datensätzen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 max-w-md">
            <Input value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="Neues Team…" onKeyDown={(e) => e.key === "Enter" && addTeam()} data-testid="new-team-input" />
            <Button onClick={addTeam} data-testid="add-team-btn"><Plus className="w-4 h-4 mr-1" />Hinzufügen</Button>
          </div>
          {teams.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {teams.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1 text-sm" data-testid={`team-chip-${t.id}`}>
                  {t.name} <span className="text-xs text-muted-foreground">({t.member_count || 0})</span>
                  <button onClick={() => removeTeam(t.id)} className="text-muted-foreground hover:text-destructive" data-testid={`team-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Benutzer ({users.length})
          </CardTitle>
          <CardDescription>
            Neue Benutzer werden per Einladungslink angelegt und setzen ihr Passwort selbst.
            Ausgeschiedene Mitarbeiter werden deaktiviert (Datensätze bleiben erhalten).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>Keine Benutzer vorhanden</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => {
                  const status = userStatus(user);
                  const role = user.role || "member";
                  const cfg = ROLE_BADGE[role] || ROLE_BADGE.member;
                  const Icon = cfg.icon;
                  return (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <div>{user.full_name}</div>
                          <div className="text-xs text-muted-foreground">{user.username}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={cfg.cls ? undefined : "secondary"} className={cfg.cls} data-testid={`user-role-badge-${user.id}`}>
                        <Icon className="w-3 h-3 mr-1" />
                        {ROLE_LABELS[role] || "Mitarbeiter"}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`user-team-${user.id}`}>{teamName(user.team_id)}</TableCell>
                    <TableCell data-testid={`user-status-${user.id}`}>
                      {status === "active" && (
                        <Badge className="bg-success/10 text-success border-success/20 hover:bg-success/10">
                          <CheckCircle2 className="w-3 h-3 mr-1" />Aktiv
                        </Badge>
                      )}
                      {status === "inactive" && (
                        <Badge variant="secondary" className="bg-muted text-muted-foreground">
                          <UserX className="w-3 h-3 mr-1" />Inaktiv
                        </Badge>
                      )}
                      {status === "invited" && (
                        <Badge className="bg-warning/10 text-warning border-warning/20 hover:bg-warning/10">
                          <Clock className="w-3 h-3 mr-1" />Eingeladen
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.id}`} title="Bearbeiten">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRbacUser(user)} data-testid={`rbac-user-${user.id}`} title="Rechte verwalten">
                          <Sliders className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`} title="Weitere Aktionen">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {status === "invited" && (
                              <DropdownMenuItem onClick={() => handleResendInvite(user)} data-testid={`resend-invite-${user.id}`}>
                                <Link2 className="w-4 h-4 mr-2" />Einladungslink erneuern
                              </DropdownMenuItem>
                            )}
                            {status === "active" && (
                              <DropdownMenuItem onClick={() => handleDeactivate(user)} data-testid={`deactivate-user-${user.id}`}>
                                <UserX className="w-4 h-4 mr-2" />Deaktivieren
                              </DropdownMenuItem>
                            )}
                            {status === "inactive" && (
                              <DropdownMenuItem onClick={() => handleActivate(user)} data-testid={`activate-user-${user.id}`}>
                                <UserCheck className="w-4 h-4 mr-2" />Reaktivieren
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => { setUserToDelete(user); setDeleteDialogOpen(true); }}
                              className="text-destructive focus:text-destructive"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />Endgültig löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create (invite) / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editUser ? "Benutzer bearbeiten" : "Benutzer einladen"}</DialogTitle>
            <DialogDescription>
              {editUser
                ? "Ändern Sie die Benutzerdaten. Lassen Sie das Passwort leer, um es nicht zu ändern."
                : "Legen Sie einen neuen Benutzer an. Es wird ein Einladungslink erzeugt, über den der Nutzer sein eigenes Passwort festlegt."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Vollständiger Name *</Label>
                <Input id="full_name" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="Max Mustermann" required data-testid="user-fullname-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="username">Benutzername *</Label>
                <Input id="username" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="mmustermann" required data-testid="user-username-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail *</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="max@example.com" required data-testid="user-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rolle *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger id="role" data-testid="user-role-select"><SelectValue placeholder="Rolle wählen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin" data-testid="user-role-option-admin"><div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Administrator</div></SelectItem>
                    <SelectItem value="manager" data-testid="user-role-option-manager"><div className="flex items-center gap-2"><Shield className="w-4 h-4" />Manager</div></SelectItem>
                    <SelectItem value="member" data-testid="user-role-option-member"><div className="flex items-center gap-2"><User className="w-4 h-4" />Mitarbeiter</div></SelectItem>
                    <SelectItem value="readonly" data-testid="user-role-option-readonly"><div className="flex items-center gap-2"><Eye className="w-4 h-4" />Nur Lesen</div></SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {formData.role === "admin" ? "Voller Zugriff inkl. Benutzer- & Team-Verwaltung."
                    : formData.role === "manager" ? "Verwaltet Team-Datensätze (anlegen/bearbeiten/löschen/zuweisen)."
                    : formData.role === "readonly" ? "Nur Lese-Zugriff auf Datensätze."
                    : "Eigene & Team-Datensätze anlegen/bearbeiten."}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="team">Team</Label>
                <Select value={formData.team_id || "none"} onValueChange={(value) => setFormData({ ...formData, team_id: value === "none" ? "" : value })}>
                  <SelectTrigger id="team" data-testid="user-team-select"><SelectValue placeholder="Kein Team" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Team</SelectItem>
                    {teams.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`user-team-option-${t.id}`}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {editUser && (
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort (leer lassen für keine Änderung)</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Neues Passwort" data-testid="user-password-input" />
                    <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit" disabled={saving} data-testid="save-user-btn">
                {saving ? "Speichern..." : editUser ? "Speichern" : "Einladung erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Einladungslink-Dialog */}
      <Dialog open={!!inviteLink} onOpenChange={(o) => !o && setInviteLink(null)}>
        <DialogContent data-testid="invite-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-primary" />Einladungslink</DialogTitle>
            <DialogDescription>
              Senden Sie diesen Link an <span className="font-medium">{inviteLink?.name}</span>. Darüber legt die
              Person ihr eigenes Passwort fest. Der Link ist bis {formatDate(inviteLink?.expires)} gültig.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={inviteLink?.url || ""} onFocus={(e) => e.target.select()} className="font-mono text-xs" data-testid="invite-link-input" />
            <Button type="button" onClick={copyLink} data-testid="copy-invite-link-btn"><Copy className="w-4 h-4 mr-1" />Kopieren</Button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setInviteLink(null)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RBAC-Matrix */}
      {rbacUser && (
        <RbacMatrixDialog
          open={!!rbacUser}
          onOpenChange={(o) => !o && setRbacUser(null)}
          user={rbacUser}
          onSaved={fetchUsers}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie &quot;{userToDelete?.full_name}&quot; wirklich <strong>endgültig</strong> löschen?
              Diese Aktion kann nicht rückgängig gemacht werden. In den meisten Fällen ist{" "}
              <strong>Deaktivieren</strong> die bessere Wahl, damit verknüpfte Datensätze erhalten bleiben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-user-btn">
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
